/**
 * Lightweight artist browse index — counts + sample cover keys only.
 * Cached per catalog fingerprint so tab remounts do not rescan 7k+ albums.
 */
import { albumArtistName } from './albumArtistFilter';

function pickSampleAlbumFields(album) {
  return {
    embedded_cover_hash: album?.embedded_cover_hash || null,
    folder_cover_hash: album?.folder_cover_hash || null,
    cover_art_thumb_hash: album?.cover_art_thumb_hash || null,
    album_key: album?.album_key || album?.id || null,
    cover_art_url: album?.cover_art_url || album?.coverUrl || null,
  };
}

/** @param {Array<object>} displayAlbums */
export function buildArtistBrowseIndex(displayAlbums) {
  const map = {};
  for (const a of displayAlbums || []) {
    const name = albumArtistName(a);
    if (!map[name]) {
      map[name] = {
        name,
        albumCount: 0,
        trackCount: 0,
        sampleAlbums: [],
      };
    }
    map[name].albumCount += 1;
    map[name].trackCount += (a.track_count || 0);
    if (map[name].sampleAlbums.length < 4) {
      map[name].sampleAlbums.push(pickSampleAlbumFields(a));
    }
  }
  return Object.values(map);
}

let cachedKey = '';
let cachedRawIndex = null;

function buildCacheKey(catalogFingerprint, albumCount) {
  return `${String(catalogFingerprint ?? '')}:${albumCount ?? 0}`;
}

/**
 * Memoized raw artist rows keyed by catalog revision — zero work on re-entry.
 */
export function getArtistBrowseIndex(displayAlbums, catalogFingerprint) {
  const albumCount = displayAlbums?.length ?? 0;
  const key = buildCacheKey(catalogFingerprint, albumCount);
  if (cachedRawIndex && cachedKey === key) {
    return cachedRawIndex;
  }
  const next = buildArtistBrowseIndex(displayAlbums);
  cachedKey = key;
  cachedRawIndex = next;
  return next;
}

/** @internal test helper */
export function resetArtistBrowseIndexCacheForTests() {
  cachedKey = '';
  cachedRawIndex = null;
}
